import React from 'react';

/** Shared branded header for procurement documents - matches PurchaseReceipt.jsx styling. */
export const DocumentHeader = ({ title, subtitle, badgeLabel, badgeColor = 'bg-blue-600' }) => (
  <>
    <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white px-10 py-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img
            src="/logo.png"
            alt="Puffin Group Logo"
            className="h-12 w-auto bg-white p-1.5 rounded-lg shadow-lg flex-shrink-0"
            crossOrigin="anonymous"
          />
          <div>
            <h1 className="text-2xl font-bold tracking-tight leading-tight">PUFFIN GROUP LTD</h1>
            <p className="text-blue-200 text-sm">Enterprise Resource Planning System</p>
            <p className="text-blue-300 text-xs mt-0.5">Kano, Nigeria</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="bg-white p-1.5 rounded-lg shadow-lg mb-2 inline-block">
            <img
              src="/qrcode.png"
              alt="QR Code"
              className="w-16 h-16 object-contain"
              crossOrigin="anonymous"
            />
          </div>
          <p className="text-xs text-blue-200">Scan for verification</p>
        </div>
      </div>
    </div>

    <div className="bg-gray-50 border-b-4 border-blue-600 px-10 py-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-1">{title}</h2>
          <p className="text-gray-600">Official Procurement Document</p>
        </div>
        {badgeLabel && (
          <div className={`${badgeColor} text-white px-6 py-3 rounded-lg shadow-md`}>
            <p className="text-lg font-bold uppercase tracking-wide">{badgeLabel}</p>
          </div>
        )}
      </div>
      {subtitle && <p className="text-sm text-gray-500 mt-2">{subtitle}</p>}
    </div>
  </>
);

/** Shared section wrapper matching the Receipt's bordered-section styling. */
export const DocumentSection = ({ title, children }) => (
  <div className="px-10 py-8 border-b border-gray-200">
    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600">{title}</h3>
    {children}
  </div>
);

const GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };

/**
 * Some legacy address fields were saved as a stringified `{ text, ... }`
 * object instead of a plain string. Unwrap that so it never shows as raw
 * JSON in a rendered document.
 */
const displayValue = (value) => {
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') return parsed.text ?? value;
    } catch {
      // Not JSON - fall through and show the raw string.
    }
  }
  if (value && typeof value === 'object') return value.text ?? JSON.stringify(value);
  return value;
};

/** Shared label/value grid matching the Receipt's info-grid styling. */
export const DocumentInfoGrid = ({ pairs, columns = 2 }) => (
  <div className={`grid ${GRID_COLS[columns] || GRID_COLS[2]} gap-6`}>
    {pairs.filter(([, value]) => value !== undefined).map(([label, value], i) => (
      <div key={i}>
        <p className="text-xs text-gray-600 font-semibold uppercase mb-1">{label}</p>
        <p className="text-base font-semibold text-gray-900">{displayValue(value) || '—'}</p>
      </div>
    ))}
  </div>
);

/** Shared items table matching the Receipt's table styling. */
export const DocumentTable = ({ headers, rows, alignRight = [] }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-blue-900 text-white">
          {headers.map((h, i) => (
            <th
              key={i}
              className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide ${alignRight.includes(i) ? 'text-right' : 'text-left'}`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={headers.length} className="px-3 py-4 text-center text-gray-400 text-sm">
              No items recorded
            </td>
          </tr>
        ) : (
          rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={`px-3 py-2.5 border-t border-gray-200 text-gray-800 ${alignRight.includes(ci) ? 'text-right font-medium' : ''}`}
                >
                  {cell ?? ''}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/** Shared signature block matching the Receipt's footer signature styling. */
export const DocumentSignatureBlock = ({ labels }) => (
  <div className={`grid gap-10 mt-8`} style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}>
    {labels.map((label, i) => (
      <div key={i}>
        <div className="border-t-2 border-gray-400 pt-2">
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          <p className="text-xs text-gray-500 mt-1">Date: ______________</p>
        </div>
      </div>
    ))}
  </div>
);

/** Shared footer matching the Receipt's footer styling. */
export const DocumentFooter = ({ documentId }) => (
  <div className="bg-gray-100 px-10 py-8 text-center border-t-4 border-blue-600">
    <div className="mb-4">
      <p className="text-sm font-bold text-gray-900 mb-2">PUFFIN GROUP LIMITED</p>
      <p className="text-xs text-gray-600">Enterprise Resource Planning System</p>
      <p className="text-xs text-gray-600">Procurement Document Management Module</p>
    </div>
    <div className="border-t border-gray-300 pt-4 mt-4">
      <p className="text-xs text-gray-500">
        This is an official computer-generated document. No signature is required unless stated.
      </p>
      <p className="text-xs text-gray-500 mt-1">
        Document ID: {documentId} | Generated:{' '}
        {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>
    </div>
  </div>
);

/** Modal shell (buttons + white A4 canvas) shared by every procurement document view. */
export const DocumentModalShell = ({ onDownload, onClose, downloading, children }) => (
  <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-[60] flex items-start justify-center py-8">
    <div className="w-[210mm] mx-auto">
      <div className="flex justify-end gap-3 mb-4">
        <button
          onClick={onDownload}
          disabled={downloading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50"
        >
          {downloading ? 'Generating PDF...' : 'Download PDF'}
        </button>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
        >
          Close
        </button>
      </div>
      <div id="procurement-document-content" className="bg-white shadow-2xl min-h-[297mm]">
        {children}
      </div>
    </div>
  </div>
);
